import React, { useRef, useState, useCallback } from 'react';
import { FiTrash2 } from 'react-icons/fi';
import {
  relativeToScreen,
  relativeSizeToScreen,
  screenToRelative,
  screenSizeToRelative,
} from '../utils/coordinateConversion';

const Field = ({
  field,
  isSelected,
  containerSize,
  pdfDimensions,
  onSelect,
  onPositionUpdate,
  onSizeUpdate,
  onDelete,
  onFieldUpdate,
  hideBox = false,
}) => {
  const fieldRef = useRef(null);
  const canvasRef = useRef(null);
  const [dragState, setDragState] = useState({
    isDragging: false,
    isResizing: false,
    resizeHandle: null,
  });
  const [inputValue, setInputValue] = useState(field.value || '');
  const [isDrawing, setIsDrawing] = useState(false);
  const [showSignatureCanvas, setShowSignatureCanvas] = useState(false);
  const [labelText, setLabelText] = useState(field.label || field.type);
  const [isEditingLabel, setIsEditingLabel] = useState(false);

  // Convert relative position/size to screen pixels for rendering
  const screenPos = relativeToScreen(
    field.position.x,
    field.position.y,
    containerSize.width,
    containerSize.height
  );

  const screenSize = relativeSizeToScreen(
    field.size.width,
    field.size.height,
    containerSize.width,
    containerSize.height
  );

  // Handle mouse down for dragging
  const handleMouseDown = (e) => {
    // Don't drag if signature canvas is open
    if (showSignatureCanvas) {
      e.stopPropagation();
      return;
    }

    // Don't drag if clicking on input or button
    if (
      e.target.tagName === 'INPUT' ||
      e.target.tagName === 'BUTTON' ||
      e.target.closest('button')
    ) {
      e.stopPropagation();
      return;
    }

    if (e.target.classList.contains('resize-handle')) {
      const handle = e.target.className.match(/resize-\w+/)[0];
      setDragState({
        isDragging: false,
        isResizing: true,
        resizeHandle: handle,
        startX: e.clientX,
        startY: e.clientY,
        startWidth: screenSize.width,
        startHeight: screenSize.height,
      });
      e.preventDefault();
      return;
    }

    setDragState({
      isDragging: true,
      isResizing: false,
      startX: e.clientX,
      startY: e.clientY,
      startPosX: screenPos.x,
      startPosY: screenPos.y,
    });

    onSelect();
    e.preventDefault();
  };

  // Handle mouse move (drag or resize)
  const handleMouseMove = useCallback(
    (e) => {
      const container = fieldRef.current?.parentElement;
      if (!container) return;

      if (dragState.isDragging) {
        const deltaX = e.clientX - dragState.startX;
        const deltaY = e.clientY - dragState.startY;

        const newScreenX = dragState.startPosX + deltaX;
        const newScreenY = dragState.startPosY + deltaY;

        // Allow free movement across the entire container
        // Don't clamp - let fields go anywhere
        const relPos = screenToRelative(
          newScreenX,
          newScreenY,
          containerSize.width,
          containerSize.height
        );

        onPositionUpdate(relPos);
      } else if (dragState.isResizing) {
        const deltaX = e.clientX - dragState.startX;
        const deltaY = e.clientY - dragState.startY;

        let newWidth = dragState.startWidth;
        let newHeight = dragState.startHeight;

        // Handle resize based on which corner/edge
        if (dragState.resizeHandle.includes('right')) {
          newWidth = Math.max(50, dragState.startWidth + deltaX);
        }
        if (dragState.resizeHandle.includes('bottom')) {
          newHeight = Math.max(30, dragState.startHeight + deltaY);
        }

        // Convert back to relative units
        const relSize = screenSizeToRelative(
          newWidth,
          newHeight,
          containerSize.width,
          containerSize.height
        );

        onSizeUpdate(relSize);
      }
    },
    [
      dragState,
      containerSize,
      screenPos,
      screenSize,
      onPositionUpdate,
      onSizeUpdate,
    ]
  );

  // Handle mouse up (end drag/resize)
  const handleMouseUp = useCallback(() => {
    setDragState({
      isDragging: false,
      isResizing: false,
    });
  }, []);

  // Add event listeners
  React.useEffect(() => {
    if (dragState.isDragging || dragState.isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState, containerSize, screenPos, screenSize, handleMouseMove, handleMouseUp]);

  const getFieldColor = () => {
    const colors = {
      signature: '#3B82F6',
      text: '#10B981',
      image: '#F59E0B',
      date: '#8B5CF6',
      radio: '#EC4899',
      checkbox: '#14B8A6',
    };
    return colors[field.type] || '#6B7280';
  };

  const handleSignatureClick = () => {
    setShowSignatureCanvas(true);
  };

  const handleSignatureComplete = () => {
    if (canvasRef.current) {
      const imageData = canvasRef.current.toDataURL('image/png');
      if (onFieldUpdate) {
        onFieldUpdate(field.id, { value: imageData });
      }
      setShowSignatureCanvas(false);
    }
  };

  const handleSignatureClear = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const handleCanvasMouseDown = (e) => {
    e.stopPropagation();
    setIsDrawing(true);
    const rect = canvasRef.current.getBoundingClientRect();
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = getFieldColor();
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const handleCanvasMouseMove = (e) => {
    if (!isDrawing) return;
    e.stopPropagation();
    const rect = canvasRef.current.getBoundingClientRect();
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const handleCanvasMouseUp = (e) => {
    e.stopPropagation();
    setIsDrawing(false);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      if (onFieldUpdate) {
        onFieldUpdate(field.id, { value: event.target.result });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDateChange = (e) => {
    if (onFieldUpdate) {
      onFieldUpdate(field.id, { value: e.target.value });
    }
  };

  const handleTextChange = (e) => {
    setInputValue(e.target.value);
    if (onFieldUpdate) {
      onFieldUpdate(field.id, { value: e.target.value });
    }
  };

  const handleCheckboxChange = (e) => {
    if (onFieldUpdate) {
      onFieldUpdate(field.id, { value: e.target.checked });
    }
  };

  const handleRadioChange = (e) => {
    if (onFieldUpdate) {
      onFieldUpdate(field.id, { value: e.target.checked });
    }
  };

  return (
    <div
      ref={fieldRef}
      className={`field ${isSelected ? 'selected' : ''}`}
      style={{
        position: 'absolute',
        left: `${screenPos.x}px`,
        top: `${screenPos.y}px`,
        width: `${screenSize.width}px`,
        height: `${screenSize.height}px`,
        border: !hideBox && isSelected ? `2px solid ${getFieldColor()}` : hideBox ? 'none' : `1px dashed #ccc`,
        backgroundColor: hideBox ? 'transparent' : `${getFieldColor()}15`,
        cursor: dragState.isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: '500',
        color: getFieldColor(),
        overflow: 'visible',
        flexDirection: 'column',
        pointerEvents: hideBox ? 'none' : 'auto',
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Delete button - always visible at top right */}
      {!hideBox && (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        title="Delete this field"
        style={{
          position: 'absolute',
          top: '-24px',
          right: '-2px',
          padding: '4px 6px',
          backgroundColor: '#EF4444',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '12px',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
          fontWeight: 'bold',
        }}
      >
        <FiTrash2 size={14} /> ✕
      </button>
      )}

      {/* Field label */}
      {!hideBox && (
      <div
        style={{
          position: 'absolute',
          top: '-20px',
          left: '0',
          fontSize: '10px',
          fontWeight: 'bold',
          color: getFieldColor(),
          backgroundColor: 'white',
          padding: '1px 4px',
          borderRadius: '3px',
          border: `1px solid ${getFieldColor()}`,
        }}
      >
        {field.type.toUpperCase()}
      </div>
      )}

      {/* Signature field */}
      {field.type === 'signature' && (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          {showSignatureCanvas ? (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                padding: '2px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <canvas
                ref={canvasRef}
                width={screenSize.width - 8}
                height={screenSize.height - 28}
                onMouseDown={handleCanvasMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseUp}
                style={{
                  border: '1px solid #999',
                  cursor: 'crosshair',
                  backgroundColor: 'white',
                  flex: 1,
                  borderRadius: '2px',
                }}
              />
              <div style={{ display: 'flex', gap: '2px', justifyContent: 'center', fontSize: '9px' }}>
                <button
                  onClick={handleSignatureClear}
                  style={{
                    padding: '2px 4px',
                    fontSize: '8px',
                    backgroundColor: '#F59E0B',
                    color: 'white',
                    border: 'none',
                    borderRadius: '2px',
                    cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
                <button
                  onClick={handleSignatureComplete}
                  style={{
                    padding: '2px 4px',
                    fontSize: '8px',
                    backgroundColor: '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '2px',
                    cursor: 'pointer',
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          ) : field.value ? (
            <img
              src={field.value}
              alt="signature"
              style={{ maxWidth: '95%', maxHeight: '95%' }}
            />
          ) : (
            <button
              onClick={handleSignatureClick}
              style={{
                padding: '4px 8px',
                fontSize: '10px',
                backgroundColor: getFieldColor(),
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: 'pointer',
                fontWeight: 'bold',
              }}
            >
              Sign
            </button>
          )}
        </div>
      )}

      {/* Text field */}
      {field.type === 'text' && (
        <input
          type="text"
          value={inputValue}
          onChange={handleTextChange}
          placeholder="Type here..."
          style={{
            width: '95%',
            height: '90%',
            border: 'none',
            backgroundColor: 'transparent',
            fontSize: '11px',
            padding: '2px 4px',
            outline: 'none',
            color: getFieldColor(),
          }}
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {/* Image field */}
      {field.type === 'image' && (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {field.value ? (
            <img
              src={field.value}
              alt="uploaded"
              style={{ maxWidth: '90%', maxHeight: '90%' }}
            />
          ) : (
            <label
              style={{
                cursor: 'pointer',
                textAlign: 'center',
                fontSize: '11px',
                fontWeight: 'bold',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
              Upload
            </label>
          )}
        </div>
      )}

      {/* Date field */}
      {field.type === 'date' && (
        <input
          type="date"
          value={field.value || ''}
          onChange={handleDateChange}
          style={{
            width: '95%',
            height: '80%',
            border: 'none',
            backgroundColor: 'transparent',
            fontSize: '10px',
            padding: '2px 4px',
            outline: 'none',
            color: getFieldColor(),
          }}
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {/* Checkbox field */}
      {field.type === 'checkbox' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
            width: '100%',
            height: '100%',
            justifyContent: 'flex-start',
            paddingLeft: '4px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={field.value || false}
            onChange={handleCheckboxChange}
            style={{
              width: '16px',
              height: '16px',
              minWidth: '16px',
              cursor: 'pointer',
              margin: 0,
              flexShrink: 0,
            }}
          />
          {isEditingLabel ? (
            <input
              type="text"
              value={labelText}
              onChange={(e) => setLabelText(e.target.value)}
              onBlur={() => {
                setIsEditingLabel(false);
                if (onFieldUpdate) {
                  onFieldUpdate(field.id, { label: labelText });
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setIsEditingLabel(false);
                  if (onFieldUpdate) {
                    onFieldUpdate(field.id, { label: labelText });
                  }
                }
              }}
              autoFocus
              style={{
                flex: 1,
                border: '1px solid #999',
                padding: '2px 4px',
                fontSize: '10px',
                borderRadius: '2px',
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span
              style={{
                fontSize: '10px',
                flex: 1,
                cursor: 'text',
                userSelect: 'none',
              }}
              onDoubleClick={() => setIsEditingLabel(true)}
              title="Double-click to edit label"
            >
              {labelText || 'Agree'}
            </span>
          )}
        </div>
      )}

      {/* Radio field */}
      {field.type === 'radio' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            width: '100%',
            height: '100%',
            justifyContent: 'flex-start',
            paddingLeft: '4px',
            position: 'relative',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="radio"
            checked={field.value || false}
            onChange={handleRadioChange}
            name={field.groupName || `radio-${field.id}`}
            style={{
              width: '16px',
              height: '16px',
              minWidth: '16px',
              cursor: 'pointer',
              margin: 0,
              flexShrink: 0,
            }}
          />
          {isEditingLabel ? (
            <input
              type="text"
              value={labelText}
              onChange={(e) => setLabelText(e.target.value)}
              onBlur={() => {
                setIsEditingLabel(false);
                if (onFieldUpdate) {
                  onFieldUpdate(field.id, { label: labelText });
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setIsEditingLabel(false);
                  if (onFieldUpdate) {
                    onFieldUpdate(field.id, { label: labelText });
                  }
                }
              }}
              autoFocus
              style={{
                flex: 1,
                border: '1px solid #999',
                padding: '2px 4px',
                fontSize: '10px',
                borderRadius: '2px',
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span
              style={{
                fontSize: '10px',
                flex: 1,
                cursor: 'text',
                userSelect: 'none',
              }}
              onDoubleClick={() => setIsEditingLabel(true)}
              title="Double-click to edit label"
            >
              {labelText || 'Option'}
            </span>
          )}
        </div>
      )}

      {/* Resize handles - only show when selected and not hiding box */}
      {isSelected && !hideBox && (
        <>
          <div
            className="resize-handle resize-se"
            style={{
              position: 'absolute',
              bottom: '-4px',
              right: '-4px',
              width: '10px',
              height: '10px',
              backgroundColor: getFieldColor(),
              borderRadius: '50%',
              cursor: 'nwse-resize',
              border: '2px solid white',
              zIndex: 999,
            }}
            onMouseDown={handleMouseDown}
          />
        </>
      )}
    </div>
  );
};

export default Field;
